import { Spinner, Typography } from '@material-tailwind/react'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import TableLayout from '../../../layouts/pagesLayouts/TableLayout'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import GraphConsumption from './GraphConsumption'
import { isIterableArray, saveGraphToPicture, toFormatPrice, toStringPrice } from '../../../utils/utils'
import FilterByMonth from './FilterByMonth' 
import { useViewConsumptionDayQuery } from '../../../store/auth/authService'
import { classCharContainerHeight, isShowInLegend, dayFormatChange, dayFormatFilter, dayFormatView, monthFormatFilter, isData } from '../common'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import axios from 'axios'
import { baseApi } from '../../../ConfigApi'
import { errorToast } from '../../../components/toasts/toasts'
import fileDownload from 'js-file-download'
import { chartColors } from '../../../utils/variables'
import { ConsByDayFormType } from '../common.types'

const GRAPH_ID = "js-cons-by-day-graph"

const ViewByDay = ({ periodDay, setPeriodDay, periodMonth, setPeriodMonth, isMobile }: {
    periodDay: any,
    setPeriodDay: any,
    setPeriodMonth: any,
    periodMonth: string,
    isMobile: boolean
}) => {
    const { t } = useTranslation()
    const { param_contract_id } = useParams<{
        param_contract_id: string;
    }>()

    const { token } = useSelector((state: RootState) => state.auth)
    const { or_number } = useSelector((state: RootState) => state.contract)


    const [sendForm, setSendForm] = useState<ConsByDayFormType>({
        contract_id: Number(param_contract_id) || 0,
        period: periodMonth,
        day: Number(moment(periodDay, dayFormatFilter).format('DD'))
    });

    const { currentData, error, isFetching } = useViewConsumptionDayQuery<any>(sendForm, {
        skip: periodMonth === '',
    })

    const uniqueKeys = currentData?.legendKeys?.map((i: string) => {
        return ({
            key: i,
            show: true,
        })
    })
    const [legend, setLegend] = useState([]);
    useEffect(() => {
        if (isIterableArray(uniqueKeys) && !isIterableArray(legend)) {
            setLegend(uniqueKeys)
        }
    }, [uniqueKeys, legend]);




    const labels = Array.from(Array(24).keys()).map(item => ++item)

    const datachar = useCallback(
        () => {
            const data = currentData?.formatted
            if (!data) return
            const datasets = []
            if (isData(data.declared_delta_less_6) && isShowInLegend(legend, 'declared_delta_less_6')) datasets.push({
                animation: false,
                transitions: false,
                order: 2,
                label: t('analytics.declared_delta_less_6'),
                data: data.declared_delta_less_6,
                backgroundColor: chartColors.declared_delta_less_6,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                }
            })
            if (isData(data.declared_delta_more_6) && isShowInLegend(legend, 'declared_delta_more_6')) datasets.push({
                animation: false,
                transitions: false,
                order: 2,
                label: t('analytics.declared_delta_more_6'),
                data: data.declared_delta_more_6,
                backgroundColor: chartColors.declared_delta_more_6,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: false,
                }
            })
            if (isData(data.data_declared) && isShowInLegend(legend, 'data_declared')) datasets.push({
                animation: false,
                transitions: false,
                order: 1,
                label: t('analytics.data_declared'),
                data: data.data_declared,
                data_origin: data.declared_origin,
                backgroundColor: chartColors.data_declared,
                borderRadius: 4,
                spanGaps: true,
                stack: 'Stack1',
                datalabels: {
                    display: !isMobile,
                    align: 'center',
                    rotation: '-90',
                    anchor: 'center',
                    formatter: function (_: any, context: any) {
                        const origin_value = context?.dataset?.data_origin?.[context?.dataIndex]
                        return `${toStringPrice(origin_value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#FDFDFD';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }
            })
            if (isData(data.data_cons) && isShowInLegend(legend, 'data_cons')) datasets.push({
                animation: false,
                transitions: false,
                order: 1,
                label: t('analytics.data_cons'),
                data: data.data_cons,
                backgroundColor: chartColors.data_cons,
                borderRadius: 4,
                spanGaps: false,
                stack: 'Stack2',
                datalabels: {
                    display: !isMobile,
                    align: 'center',
                    rotation: '-90',
                    anchor: 'center',
                    formatter: function (value: any) {
                        return `${toStringPrice(value)}`;
                    },
                    color: function (context: any) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        return '#444248';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }

            })
            if (data) return {
                labels: labels,
                datasets: datasets,
            }
        },
        [currentData?.formatted, legend],
    )

    const setFormForFetch = (values: any) => {
        const newMonthPeriod = moment(values.period, dayFormatFilter).format(monthFormatFilter)
        const newday = moment(values.period, dayFormatFilter).format('DD')
        setPeriodMonth(newMonthPeriod)
        setPeriodDay(values.period)
        setSendForm({
            day: Number(newday),
            period: newMonthPeriod,
            contract_id: Number(param_contract_id)
        })
    }

    const titleTooltipFormatter = (val: string) => {
        return ''
    }

    const saveExcel = (callback?: any) => {
        axios.post(`${baseApi}/api/analitic/build-excel-report-type-9`, {
            ...sendForm,
            typeReport: 9,
        },
            {
                responseType: 'blob',
                headers: {
                    'Authorization': token,
                }
            })
            .then((response: any) => {
                if (response.data.res === 'Error') {
                    errorToast({
                        title: t('analytics.build_excel_report'),
                        text: t('analytics.build_excel_report_error')
                    })
                }
                else {
                    fileDownload(response.data, `${t('analytics.build_excel_report_9')} ${sendForm.day}-${sendForm.period}.xlsx`)
                }
            })
            .catch(error => {
                console.error(error);
                errorToast({
                    title: t('analytics.build_excel_report'),
                    text: t('analytics.build_excel_report_error')
                })
            })
            .finally(() => {
                if (callback) callback()
            })
    }

    return (

        // ${periodDay ? 'opacity- 1 block' : 'opacity- 0 hidden'} 
        <div className={` 
        transition-opacity delay-[150ms] duration-[250ms]`}>


            <FilterByMonth
                formatView={dayFormatView}
                formatFilter={dayFormatFilter}
                formatChange={dayFormatChange}
                value={{
                    period: periodDay,
                }}
                isLoadingFetch={false}
                fetchPage={setFormForFetch}
                savePicture={() => {
                    saveGraphToPicture(GRAPH_ID, `${t('or')} ${or_number} - ${t('analytics.analytics_cons_by_day_title')} ${t('by_period')} ${periodDay}.png`)
                }}
                saveExcel={saveExcel}
                isForSave={!!currentData?.isForSave}
            />
            {

                currentData ?
                    <>
                        <div className='bg-white border-2 border-gray-200 rounded-[16px] lap:rounded-[24px] p-[24px] lap:p-[32px]'>
                            <Typography variant='lead' className='text-gray-800 text-center mb-[24px] tab:mb-[32px] text-[20px] lap:text-[24px] text-left'>
                                {t('analytics.analytics_cons_by_day_title')}
                            </Typography>
                            <div className={classCharContainerHeight}>
                                <GraphConsumption
                                    graphId={GRAPH_ID}
                                    titleTooltipFormatter={titleTooltipFormatter}
                                    datachar={datachar()}
                                    fixedCount={0}
                                />
                            </div>
                            {
                                !!currentData?.isForSave &&
                                    (isIterableArray(uniqueKeys) && isIterableArray(legend))
                                    ?
                                    <div className=' mt-[24px] flex gap-[8px] lap:gap-x-[16px] flex-wrap justify-start'>
                                        {
                                            legend.map((item: any, index: number) => (
                                                <div key={index} className='flex gap-[2px] items-center cursor-pointer' onClick={() => {
                                                    setLegend((prev: any) => prev.map((i: any) => ({
                                                        ...i,
                                                        show: item.key === i.key ? !i.show : i.show
                                                    })))
                                                }}>
                                                    <span className={`block rounded w-[10px] h-[10px]  `} style={{ backgroundColor: item.show ? chartColors?.[item.key] : '#eee' }}></span>
                                                    <Typography variant='lead' className={`${item.show ? 'text-gray-700' : 'text-gray-400'}  font-medium text-[12px] lap:text-[14px]`}>
                                                        {t(`analytics.${item.key}`)}
                                                    </Typography>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    :
                                    <div className='min-h-[45px]' />
                            }
                        </div>
                    </>
                    :
                    isFetching ?
                        <div className={`${classCharContainerHeight} bg-white border-2 border-gray-200 rounded-[16px] lap:rounded-[24px] p-[24px] lap:p-[32px] flex justify-center items-center`}>
                            <Spinner />
                        </div>
                        :
                        error ?
                            <TableLayout>
                                <Typography variant='lead' className='text-[16px] lap:text-[20px] font-medium text-center my-[40px]'>
                                    {t('tables.error_on_loading')}
                                </Typography>
                            </TableLayout>
                            :
                            ''
            }
        </div>
    )
}

export default ViewByDay